import { type TFunction } from 'i18next'

import { isImageQuestionText } from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import { exists } from '~/utils'

import {
  buildCustomerAnalysisTableElements,
  buildCustomerAttributeAxisTableElements,
  buildCustomizedAnalysisTableElements,
  buildQuestionTableElements,
  type TableElement,
} from './buildTableElements'
import { type RenderTask } from './planRenderTasks'
import { type BasicOutputsResult } from './useGenerateBasicOutputs'

type TaskElementsBuilderContext = {
  customerAttributeGroups: {
    customerAttributes: { id: string; name: string }[]
    id: string
    name: string
  }[]
  // Wizard-context questions (standard analysis per-question filtering).
  questions: {
    mt_questions: string[]
    question_id: string
    question_text: string
  }[]
  result: BasicOutputsResult
  // Selected brands in the SAME order the analyzer resolver builds its group
  // filters (= planRenderTasks `targetBrands`). Threaded into
  // buildQuestionTableElements so the (brand,segment)→index mapping is
  // reconstructed from brand position, not from answer rows.
  selectedBrandsInOrder: { id: number; number: number }[]
  // Full survey questions (customized analysis matrix partitioning).
  surveyQuestions: Parameters<
    typeof buildCustomizedAnalysisTableElements
  >[0]['questions']
  t: TFunction
}

// The per-(brand,segment) respondent counts every ratio and the 分布 / サンプル数 rows divide
// by. countByCustomerAttributes returns no `groupCounts`, so reuse the customerAnalysis output
// the batch already requests once per filter pattern — it carries exactly this.
//
// Laid out the way buildNineSegmentsTotalData indexes it (the same order the analyzer builds
// its group filters): brandPos * 9 + (segmentNumber - 1). Anything the engine omitted stays
// NaN so missing data surfaces instead of reading as zero.
const buildGroupCountsFromCustomerAnalysis = (
  allOutputs: BasicOutputsResult['filterResults'][number]['brandResults'][number]['outputs'],
  selectedBrandsInOrder: { id: number }[],
  filterIndex: number,
): number[] => {
  const customerAnalysis = allOutputs.find(
    (o) => o.__typename === 'Brands9segsInBrandGroupOutputResult',
  )
  if (
    !exists(customerAnalysis) ||
    customerAnalysis.__typename !== 'Brands9segsInBrandGroupOutputResult'
  ) {
    throw new Error(
      `basic-output: customerAnalysis output missing, cannot derive the customer-attribute denominator (filterIndex=${filterIndex})`,
    )
  }

  const groupCounts = new Array<number>(selectedBrandsInOrder.length * 9).fill(
    NaN,
  )
  selectedBrandsInOrder.forEach((selectedBrand, brandPos) => {
    const brandResult = (customerAnalysis.result.result ?? []).find(
      ({ id }) => id === selectedBrand.id,
    )
    for (const { count, segmentNumber } of brandResult?.result ?? []) {
      groupCounts[brandPos * 9 + (segmentNumber - 1)] = count
    }
  })

  return groupCounts
}

// Builds the table elements for one RenderTask from the engine result, memoizing
// per (filterIndex, kind, role, brandId) so the (5segs, 9segs) pair is built ONCE
// and reused by both segment tasks. A missing output or builder throw is a
// genuine missing-engine-data error: FAIL FAST, never swallow — planRenderTasks
// fixes the count, so a build failure is a real error, not a silent count drop.
//
// Shared by useRenderAndSaveDocumentImages (renders each element to an image) and
// useGenerateAnalysisComments (reads each element's textContent for the prompt),
// so both phases build IDENTICAL tables from the same engine result.
export const createTaskElementsBuilder = ({
  customerAttributeGroups,
  questions,
  result,
  selectedBrandsInOrder,
  surveyQuestions,
  t,
}: TaskElementsBuilderContext): ((task: RenderTask) => TableElement[]) => {
  const builtElementsCache = new Map<string, TableElement[]>()

  return (task: RenderTask): TableElement[] => {
    const cacheKey = `${task.filterIndex}|${task.kind}|${task.role ?? ''}|${task.brandId ?? ''}`
    const cached = builtElementsCache.get(cacheKey)
    if (exists(cached)) {
      return cached
    }

    const filterResult = result.filterResults[task.filterIndex]
    if (!exists(filterResult)) {
      throw new Error(
        `basic-output: filter result missing for filterIndex=${task.filterIndex}`,
      )
    }
    const sampleSize = filterResult.sampleSize
    const allOutputs = filterResult.brandResults.flatMap((br) => br.outputs)

    let elements: TableElement[]
    if (task.kind === 'customer') {
      const output = allOutputs.find(
        (o) => o.__typename === 'Brands9segsInBrandGroupOutputResult',
      )
      if (!exists(output)) {
        throw new Error(
          `basic-output: customer output missing for filterIndex=${task.filterIndex}`,
        )
      }
      elements = buildCustomerAnalysisTableElements(
        output.result,
        sampleSize,
        t,
      )
    } else if (task.kind === 'standard' && task.axis === 'customerAttribute') {
      const output = allOutputs.find(
        (o) =>
          o.__typename === 'CustomerAttributeAxisOutputResult' &&
          o.outputType === `standardAnalysis_${task.role}`,
      )
      if (
        !exists(output) ||
        output.__typename !== 'CustomerAttributeAxisOutputResult'
      ) {
        throw new Error(
          `basic-output: customer attribute output missing for filterIndex=${task.filterIndex}, role=${task.role}`,
        )
      }
      if (!exists(task.brandId)) {
        throw new Error(
          `basic-output: customer attribute task missing brandId for filterIndex=${task.filterIndex}, role=${task.role}`,
        )
      }
      const brand = selectedBrandsInOrder.find(({ id }) => id === task.brandId)
      if (!exists(brand)) {
        throw new Error(
          `basic-output: brandId=${task.brandId} is not in the selected brands (filterIndex=${task.filterIndex}, role=${task.role})`,
        )
      }
      elements = buildCustomerAttributeAxisTableElements({
        brandId: task.brandId,
        brandNumber: brand.number,
        // Restrict the row blocks to the group the user actually picked; without this every
        // group in the survey would be rendered into a slot that selected only one.
        customerAttributeGroups: customerAttributeGroups.filter(
          ({ customerAttributes }) =>
            customerAttributes.some(({ id }) =>
              (task.customerAttributeIds ?? []).includes(id),
            ),
        ),
        customerAttributeResults: output.result.customerAttributes,
        groupCounts: buildGroupCountsFromCustomerAnalysis(
          allOutputs,
          selectedBrandsInOrder,
          task.filterIndex,
        ),
        sampleSize,
        selectedBrandsInOrder,
        t,
      })
    } else if (task.kind === 'standard') {
      const output = allOutputs.find(
        (o) =>
          o.__typename === 'AnswersByMultiQuestionsOutputResult' &&
          o.outputType === `standardAnalysis_${task.role}`,
      )
      if (
        !exists(output) ||
        output.__typename !== 'AnswersByMultiQuestionsOutputResult'
      ) {
        throw new Error(
          `basic-output: standard output missing for filterIndex=${task.filterIndex}, role=${task.role}`,
        )
      }
      if (!exists(task.brandId)) {
        throw new Error(
          `basic-output: standard task missing brandId for filterIndex=${task.filterIndex}, role=${task.role}`,
        )
      }
      elements = buildQuestionTableElements(
        output.result,
        task.brandId,
        sampleSize,
        t,
        task.allowedQuestionIds,
        questions,
        selectedBrandsInOrder,
        // ドライバーTop5ブロックのゲート。NPIドライバー役割はウィザードでイメージ設問と
        // 明示的に割り当てられているので常に付ける。重視点/きっかけ/抵抗 役割はイメージ設問
        // ではないので付けない (専用プロンプトはブロックを使わないが、誤誘導の芽を絶つ)。
        // それ以外 (カスタム設問役割 → bare standardAnalysis の場合分けプロンプト) は役割から
        // 種別が分からないため、設問文で判定する。
        task.role === 'npiDriver'
          ? () => true
          : task.role === 'importance' ||
              task.role === 'trigger' ||
              task.role === 'resistance'
            ? () => false
            : isImageQuestionText,
      )
    } else {
      const output = allOutputs.find(
        (o) =>
          o.__typename === 'CustomizedAnalysisOutputResult' &&
          o.outputType === `customizedAnalysis_${task.role}`,
      )
      if (
        !exists(output) ||
        output.__typename !== 'CustomizedAnalysisOutputResult'
      ) {
        throw new Error(
          `basic-output: customized output missing for filterIndex=${task.filterIndex}, role=${task.role}`,
        )
      }
      // planRenderTasks always sets it on a customized task; its absence means the
      // task did not come from planning, so the matrix partition would silently
      // fall back to 連続設問 instead of failing.
      if (!exists(task.selectedQuestionIds)) {
        throw new Error(
          `basic-output: customized task missing selectedQuestionIds for filterIndex=${task.filterIndex}, role=${task.role}`,
        )
      }
      elements = buildCustomizedAnalysisTableElements({
        questions: surveyQuestions,
        result: output.result,
        sampleSize,
        selectedQuestionIds: task.selectedQuestionIds,
        t,
      })
    }

    builtElementsCache.set(cacheKey, elements)

    return elements
  }
}
